import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Typography, Row, Col, Statistic, DatePicker, Space, Spin, Alert,
  Tag, Button, Empty, Breadcrumb,
} from 'antd';
import Table from 'components/Common/Table';
import { ArrowLeftOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer,
  Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';
import useIsMobile from '../../hooks/useIsMobile';
import useUrlFilters from '../../hooks/useUrlFilters';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import ReportGenerationModal from '../../components/Reports/ReportGenerationModal';
import {
  breakdownSummaryEntries, deriveColumns, deriveRowChart, deriveTrendChart, formatValue,
  humanizeKey, listSummaryEntries, scalarSummaryEntries,
} from '../../utils/reportFieldFormat';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

const CATEGORY_COLORS = {
  Attendance: '#1890ff',
  Leave: '#52c41a',
  Financial: '#faad14',
  Sales: '#eb2f96',
};

// The viewer asks for more rows than a page shows so sorting and the PDF cover
// the whole report; the API caps it and says when it had to truncate.
const PREVIEW_ROW_LIMIT = 2000;

const RANGE_PRESETS = [
  { key: 'this_month', label: 'This Month', range: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { key: 'last_month', label: 'Last Month', range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { key: 'last_30_days', label: 'Last 30 Days', range: () => [dayjs().subtract(30, 'day'), dayjs()] },
  { key: 'this_quarter', label: 'This Quarter', range: () => [dayjs().startOf('quarter'), dayjs().endOf('quarter')] },
  { key: 'this_year', label: 'This Year', range: () => [dayjs().startOf('year'), dayjs().endOf('year')] },
];

// The range lives in the URL so a report view can be shared as a link. Presets
// are kept by name (so "this month" stays relative); only custom carries dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'this_month' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
};

/**
 * On-screen view of any of the backend's report types.
 *
 * It reads the same generators the file exports use (`/reports/reports/preview/`),
 * so what is on screen is what downloads. Nothing here is per-report: the stat
 * tiles, charts and table columns are derived from the shape the generator
 * returns, which is why one page can serve all 17 reports.
 */
const ReportViewer = () => {
  const { reportType } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const reportRef = useRef(null);

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const activePreset = filters.preset;
  const dateRange = useMemo(() => {
    if (activePreset === 'custom') return filters.range;
    const preset = RANGE_PRESETS.find((p) => p.key === activePreset) || RANGE_PRESETS[0];
    return preset.range();
  }, [activePreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/reports/reports/preview/', {
        params: {
          report_type: reportType,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
          limit: PREVIEW_ROW_LIMIT,
        },
      });
      if (response.status === 200) setReport(response.data);
    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.response?.data?.error || 'Failed to load this report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [reportType, dateRange]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handlePreset = (preset) => {
    setFilters({ preset: preset.key, range: null });
  };

  const rows = useMemo(() => report?.data || [], [report]);
  const columns = useMemo(() => deriveColumns(rows), [rows]);
  const stats = useMemo(() => scalarSummaryEntries(report?.summary), [report]);
  const breakdowns = useMemo(() => breakdownSummaryEntries(report?.summary), [report]);
  const lists = useMemo(() => listSummaryEntries(report?.summary), [report]);
  // Reports whose summary has no breakdown still get charts, built from the rows
  const rowChart = useMemo(() => deriveRowChart(rows, columns), [rows, columns]);
  const trendChart = useMemo(() => deriveTrendChart(rows, columns), [rows, columns]);

  const tableColumns = useMemo(() => columns.map((column) => ({
    title: column.title,
    dataIndex: column.key,
    key: column.key,
    align: column.align,
    ellipsis: true,
    sorter: column.format === 'text' || column.format === 'date'
      ? (a, b) => String(a[column.key] ?? '').localeCompare(String(b[column.key] ?? ''))
      : (a, b) => (Number(a[column.key]) || 0) - (Number(b[column.key]) || 0),
    render: (value) => formatValue(value, column.format),
  })), [columns]);

  const pdfTables = useMemo(() => ([
    ...breakdowns.map((breakdown) => ({
      title: breakdown.label,
      columns: [
        { header: 'Item', key: 'name' },
        { header: 'Value', key: 'value', align: 'right', format: breakdown.format === 'currency' ? 'currency' : 'number' },
      ],
      rows: breakdown.data,
    })),
    {
      title: report?.title || 'Report Details',
      note: report?.metadata?.truncated
        ? `Showing the first ${rows.length} of ${report?.metadata?.total_records} rows.`
        : undefined,
      columns: columns.map((column) => ({
        header: column.title,
        key: column.key,
        align: column.align,
        format: column.format === 'currency' ? 'currency'
          : column.format === 'percent' ? 'percent'
            : column.format === 'number' ? 'number' : 'text',
      })),
      rows,
    },
  ]), [breakdowns, columns, rows, report]);

  const pdfMeta = useMemo(() => stats.slice(0, 6).map((stat) => ({
    label: stat.label,
    value: stat.value,
  })), [stats]);

  const category = report?.category;
  const categoryColor = CATEGORY_COLORS[category] || '#1890ff';

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5',
    }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Breadcrumb
          style={{ marginBottom: 8 }}
          items={[
            { title: <Link to="/reports/all">All Reports</Link> },
            { title: report?.report_type_display || humanizeKey(reportType) },
          ]}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div>
            <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>
              {report?.title || report?.report_type_display || humanizeKey(reportType)}
            </Title>
            {report?.description && (
              <Paragraph type="secondary" style={{ margin: 0, fontSize: isMobile ? 12 : 13 }}>
                {report.description}
              </Paragraph>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {category && <Tag color={categoryColor}>{category}</Tag>}
            <ExportPdfButton
              reportRef={reportRef}
              title={report?.title || humanizeKey(reportType)}
              subtitle={report?.metadata?.date_range}
              meta={pdfMeta}
              tables={pdfTables}
              fileName={String(reportType || 'report').replace(/_/g, '-')}
              orientation="landscape"
              disabled={loading || !report}
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => setDownloadModalOpen(true)}
              disabled={!report}
            >
              {isMobile ? '' : 'Excel / CSV'}
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport} loading={loading} />
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/all')}>
              {isMobile ? '' : 'Back'}
            </Button>
          </div>
        </div>
      </div>

      {/* Date range */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space wrap size="small">
            {RANGE_PRESETS.map((preset) => (
              <Button
                key={preset.key}
                size="small"
                type={activePreset === preset.key ? 'primary' : 'default'}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
              }
            }}
            format="YYYY-MM-DD"
            size="small"
            allowClear={false}
          />
        </Space>
      </Card>

      {error && (
        <Alert
          message="Could not load this report"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}><Text>Building report...</Text></div>
          </div>
        </Card>
      )}

      {!loading && report && (
        <>
          {report.metadata?.truncated && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Showing the first ${report.metadata.returned_records} of ${report.metadata.total_records} rows. Narrow the date range, or use Excel / CSV for the full set - the totals above cover every row.`}
            />
          )}

          {/* Summary tiles */}
          {stats.length > 0 && (
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              {stats.map((stat) => (
                <Col xs={12} sm={12} md={6} key={stat.key}>
                  <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                    <Statistic
                      title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>{stat.label}</span>}
                      value={stat.value}
                      valueStyle={{ fontSize: isMobile ? '15px' : '20px' }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* Breakdown charts, drawn from whatever the generator summarised */}
          {breakdowns.length > 0 && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {breakdowns.map((breakdown) => (
                <Col xs={24} md={breakdowns.length === 1 ? 24 : 12} key={breakdown.key}>
                  <Card size="small" title={<Text strong>{breakdown.label}</Text>}>
                    <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                      {breakdown.data.length <= 6 ? (
                        <PieChart>
                          <Pie
                            data={breakdown.data}
                            cx="50%"
                            cy="50%"
                            innerRadius={isMobile ? 35 : 50}
                            outerRadius={isMobile ? 65 : 90}
                            paddingAngle={2}
                            dataKey="value"
                            label={isMobile ? false : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          >
                            {breakdown.data.map((entry, index) => (
                              <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value) => formatValue(value, breakdown.format)} />
                          {isMobile && <Legend />}
                        </PieChart>
                      ) : (
                        <BarChart data={breakdown.data} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 11 }} angle={-35} textAnchor="end" height={60} interval={0} />
                          <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 40 : 60} />
                          <RechartsTooltip formatter={(value) => formatValue(value, breakdown.format)} />
                          <Bar dataKey="value" name={breakdown.label}>
                            {breakdown.data.map((entry, index) => (
                              <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {trendChart && (
            <Card
              size="small"
              title={<Text strong>{trendChart.label}</Text>}
              style={{ marginBottom: 16 }}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
                <BarChart data={trendChart.data} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 11 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 45 : 65} />
                  <RechartsTooltip formatter={(value) => formatValue(value, trendChart.format)} />
                  <Bar dataKey="value" name={trendChart.label} fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {rowChart && (
            <Card
              size="small"
              title={
                <Text strong>
                  {rowChart.label}
                  {rowChart.truncated && (
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal', marginLeft: 8 }}>
                      top {rowChart.data.length} of {rowChart.totalGroups}
                    </Text>
                  )}
                </Text>
              }
              style={{ marginBottom: 16 }}
            >
              <ResponsiveContainer width="100%" height={Math.max(200, rowChart.data.length * 28)}>
                <BarChart data={rowChart.data} layout="vertical" margin={{ top: 10, right: 30, left: isMobile ? 10 : 40, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={isMobile ? 90 : 160} />
                  <RechartsTooltip formatter={(value) => formatValue(value, rowChart.format)} />
                  <Bar dataKey="value" name={rowChart.label}>
                    {rowChart.data.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Plain list summaries, e.g. the departments a report touches */}
          {lists.length > 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {lists.map((list) => (
                  <div key={list.key}>
                    <Text strong style={{ marginRight: 8 }}>{list.label}:</Text>
                    {list.values.length
                      ? list.values.map((value) => <Tag key={String(value)}>{String(value)}</Tag>)
                      : <Text type="secondary">None</Text>}
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {/* Rows */}
          <Card
            size="small"
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Text strong>Details</Text>
                {!isMobile && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {report.metadata?.date_range} · {report.metadata?.total_records} record(s)
                  </Text>
                )}
              </div>
            }
          >
            {rows.length ? (
              <Table
                columns={tableColumns}
                dataSource={rows}
                rowKey={(row, index) => row.id || row.employee_id || row.invoice_number || index}
                size="small"
                pagination={{
                  defaultPageSize: isMobile ? 5 : 15,
                  pageSizeOptions: ['5', '15', '30', '50', '100'],
                  showSizeChanger: !isMobile,
                  showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                  simple: isMobile,
                }}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <Empty description="No records in this date range" style={{ padding: '24px 0' }} />
            )}
          </Card>
        </>
      )}

      {/* Server-side export in the other formats */}
      <ReportGenerationModal
        visible={downloadModalOpen}
        report={report ? {
          report_type: report.report_type,
          report_type_display: report.report_type_display,
        } : null}
        onClose={() => setDownloadModalOpen(false)}
        onSuccess={() => setDownloadModalOpen(false)}
      />
    </div>
  );
};

export default ReportViewer;
